import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import esignService from '../../../services/esign.service';
import type { SignatureTemplate, CreateEnvelopeInput, SignatureDocType } from '../../../types/esign';

interface SignerInput {
  name: string;
  email: string;
  signerIndex: number;
}

const DOC_TYPE_LABELS: Record<SignatureDocType, string> = {
  NDA: 'NDA',
  SUBSCRIPTION: 'Subscription',
  SIDE_LETTER: 'Side Letter',
  PPM_ACKNOWLEDGMENT: 'PPM Acknowledgment',
  TAX_FORM: 'Tax Form',
  ACCREDITATION: 'Accreditation',
  OTHER: 'Other',
};

export default function CreateEnvelopePage() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<SignatureTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [customFile, setCustomFile] = useState<File | null>(null);
  const [signingMode, setSigningMode] = useState<'SEQUENTIAL' | 'PARALLEL'>('SEQUENTIAL');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderFrequencyDays, setReminderFrequencyDays] = useState(3);

  const [signers, setSigners] = useState<SignerInput[]>([
    { name: '', email: '', signerIndex: 0 },
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const result = await esignService.listTemplates({ isActive: true, pageSize: 100 });
      setTemplates(result.data);
    } catch {
      // Templates are optional; a custom document can be used
    } finally {
      setLoadingTemplates(false);
    }
  };

  const addSigner = () => {
    setSigners([...signers, { name: '', email: '', signerIndex: signers.length }]);
  };

  const removeSigner = (index: number) => {
    if (signers.length <= 1) return;
    const updated = signers
      .filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, signerIndex: i }));
    setSigners(updated);
  };

  const updateSigner = (index: number, field: keyof SignerInput, value: string) => {
    const updated = [...signers];
    if (field === 'signerIndex') {
      updated[index] = { ...updated[index], [field]: parseInt(value) || 0 };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setSigners(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!selectedTemplateId && !customFile) {
      setError('Select a template or upload a document');
      return;
    }
    const invalidSigners = signers.filter((s) => !s.name.trim() || !s.email.trim());
    if (invalidSigners.length > 0) {
      setError('All signers must have a name and email');
      return;
    }

    setSubmitting(true);
    try {
      const input: CreateEnvelopeInput = {
        templateId: selectedTemplateId || undefined,
        subject: subject.trim(),
        message: message.trim() || undefined,
        signers: signers.map((s) => ({
          name: s.name.trim(),
          email: s.email.trim(),
          signerIndex: s.signerIndex,
        })),
        signingMode,
        expiresInDays,
        reminderEnabled,
        reminderFrequencyDays,
      };

      const envelope = await esignService.createEnvelope(input, customFile ?? undefined);
      navigate(`/admin/esign/envelopes/${envelope.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create envelope');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin/esign/envelopes"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Create Envelope</h1>
          <p className="text-sm text-gray-500 mt-0.5">Send a document for electronic signature</p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document Selection */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Document</h2>

          {/* Template selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                if (e.target.value) setCustomFile(null);
              }}
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
              disabled={loadingTemplates}
            >
              <option value="">
                {loadingTemplates ? 'Loading templates...' : '-- Select a template (optional) --'}
              </option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({DOC_TYPE_LABELS[t.documentType]}, {t.pageCount} pg)
                </option>
              ))}
            </select>
          </div>

          {/* Or upload custom document */}
          {!selectedTemplateId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Or Upload Document (PDF)
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setCustomFile(file);
                    setSelectedTemplateId('');
                  }
                }}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
              {customFile && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected: {customFile.name} ({(customFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          )}
        </div>

        {/* Envelope Details */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Details</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Subscription Agreement - Fund III"
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional message to signers..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400 resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signing Mode</label>
              <select
                value={signingMode}
                onChange={(e) => setSigningMode(e.target.value as 'SEQUENTIAL' | 'PARALLEL')}
                className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
              >
                <option value="SEQUENTIAL">Sequential (in order)</option>
                <option value="PARALLEL">Parallel (any order)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires In (Days)</label>
              <input
                type="number"
                min={1}
                max={365}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Frequency</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={reminderEnabled}
                    onChange={(e) => setReminderEnabled(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Enabled
                </label>
                {reminderEnabled && (
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={reminderFrequencyDays}
                    onChange={(e) => setReminderFrequencyDays(parseInt(e.target.value) || 3)}
                    className="w-20 px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
                  />
                )}
                {reminderEnabled && <span className="text-xs text-gray-500">days</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Signers */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Signers</h2>
            <button
              type="button"
              onClick={addSigner}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Signer
            </button>
          </div>

          <div className="space-y-3">
            {signers.map((signer, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full text-sm font-medium text-gray-600 flex-shrink-0 mt-0.5">
                  {idx + 1}
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                    <input
                      type="text"
                      value={signer.name}
                      onChange={(e) => updateSigner(idx, 'name', e.target.value)}
                      placeholder="Full name"
                      className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                    <input
                      type="email"
                      value={signer.email}
                      onChange={(e) => updateSigner(idx, 'email', e.target.value)}
                      placeholder="email@example.com"
                      className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
                      required
                    />
                  </div>
                </div>
                {signers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSigner(idx)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0 mt-0.5"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {signingMode === 'SEQUENTIAL' && signers.length > 1 && (
            <p className="text-xs text-gray-500">
              Signers will be notified in order (top to bottom). Each signer must complete before the next is notified.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link
            to="/admin/esign/envelopes"
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting ? 'Creating...' : 'Create Envelope'}
          </button>
        </div>
      </form>
    </div>
  );
}
